import React, { useState, useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Chip,
    Tooltip,
    Avatar
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BlockIcon from '@mui/icons-material/Block';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useTranslation } from 'core/context/TranslationContext';

function HomeJobsSidebar({ searchFilters = {}, onJobSelect, selectedJobId, onLoadingChange }) {
    const [hiddenJobIds, setHiddenJobIds] = useState(new Set());
    const observerTarget = useRef(null);
    const queryClient = useQueryClient();
    const size = 20;

    const { _t } = useTranslation();

    // Load jobs with filters using infinite query
    const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
        ['homeJobs', JSON.stringify(searchFilters)],
        async ({ pageParam = 1 }) => {
            return await JobHttpService.loadJobs({
                page: pageParam,
                size,
                filters: searchFilters
            });
        },
        {
            getNextPageParam: (lastPage, allPages) => {
                const totalLoaded = allPages.reduce((sum, page) => sum + (page.results?.length || 0), 0);
                const total = lastPage.count || 0;
                return totalLoaded < total ? allPages.length + 1 : undefined;
            },
            keepPreviousData: true,
            staleTime: 30000
        }
    );

    const jobs = data?.pages?.flatMap(page => page.results || []) || [];
    const visibleJobs = jobs.filter(job => !hiddenJobIds.has(job.id));

    // Notify parent of loading state changes
    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(isLoading || isFetching);
        }
    }, [isLoading, isFetching, onLoadingChange]);

    // Bookmark mutation
    const bookmarkMutation = useMutation(
        async (jobId) => {
            const service = new HttpService();
            return await service.post('/job/bookmark/', { job: jobId });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['homeJobs']);
            }
        }
    );

    // Hide job mutation
    const hideJobMutation = useMutation(
        async (jobId) => {
            const service = new HttpService();
            return await service.post('/job/hide/', { job: jobId });
        },
        {
            onSuccess: (_, jobId) => {
                setHiddenJobIds(prev => new Set([...prev, jobId]));
            }
        }
    );

    // Infinite scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetching) {
                    fetchNextPage();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasNextPage, isFetching, fetchNextPage]);

    const handleBookmark = (e, jobId, isBookmarked) => {
        e.stopPropagation();
        bookmarkMutation.mutate(jobId);
    };

    const handleHide = (e, jobId) => {
        e.stopPropagation();
        hideJobMutation.mutate(jobId);
    };

    const handleJobClick = (job) => {
        if (onJobSelect) {
            onJobSelect(job);
        }
    };

    const getLocation = (job) => {
        const parts = [];
        if (job.company?.city) {
            parts.push(job.company.city);
        }
        if (job.company?.country?.name) {
            parts.push(Utils.getDefaultName(job.company.country));
        }
        if (parts.length > 0) {
            return parts.join(', ');
        }
        if (job.address) {
            return job.address;
        }
        return 'Location not specified';
    };

    const getSalary = (job) => {
        if (job.salary) {
            return `$${job.salary}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        }
        if (job.pay_minimum && job.pay_maximum) {
            return `$${job.pay_minimum} - $${job.pay_maximum}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        }
        return null;
    };

    const AVATAR_COLORS = [
        '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828',
        '#00796b', '#5d4037', '#0288d1', '#512da8', '#d81b60'
    ];

    const nameToColor = (name) => {
        let hash = 0;
        for (let i = 0; i < (name?.length || 0); i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    };

    const getCompanyLogoUrl = (company) => {
        if (!company?.image) return null;
        if (company.image.startsWith('http')) return company.image;
        if (company.image.startsWith('/')) return HttpService.SiteBase + company.image;
        return `${HttpService.SiteBase}/${company.image}`;
    };

    if (isLoading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (visibleJobs.length === 0 && !isLoading) {
        return (
            <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        No jobs found. Try adjusting your search filters.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" className="kfont" color="text.secondary">
                    {data?.pages?.[0]?.count || 0} {_t("jobs found")}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {visibleJobs.map((job, index) => {
                    const isSelected = selectedJobId === job.id;
                    const isBookmarked = job.is_bookmarked || false;
                    const salary = getSalary(job);

                    return (
                        <Card
                            key={job.id}
                            className="jf-job-card"
                            onClick={() => handleJobClick(job)}
                            sx={{
                                cursor: 'pointer',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                boxShadow: isSelected ? '0 0 0 3px rgba(74,91,176,.14)' : undefined,
                                '&:hover .job-actions': { opacity: 1 }
                            }}
                        >
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    {(() => {
                                        const logoUrl = getCompanyLogoUrl(job.company);
                                        const title = job.company?.title || '';
                                        const initial = title.charAt(0).toUpperCase() || '?';
                                        return (
                                            <Avatar
                                                variant="rounded"
                                                src={logoUrl || undefined}
                                                alt={title}
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    flexShrink: 0,
                                                    bgcolor: logoUrl ? 'grey.100' : nameToColor(title),
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 700,
                                                    color: '#fff',
                                                    '& .MuiAvatar-img': {
                                                        objectFit: 'contain',
                                                        objectPosition: 'center'
                                                    }
                                                }}
                                            >
                                                {initial}
                                            </Avatar>
                                        );
                                    })()}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={700}
                                                sx={{
                                                    lineHeight: 1.3,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {Utils.normalizeText(job.title)}
                                            </Typography>
                                            <Box
                                                className="job-actions"
                                                sx={{
                                                    display: 'flex',
                                                    gap: 0,
                                                    flexShrink: 0,
                                                    opacity: isBookmarked ? 1 : 0,
                                                    transition: 'opacity 0.15s'
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleBookmark(e, job.id, isBookmarked)}
                                                    color={isBookmarked ? 'primary' : 'default'}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    {isBookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
                                                </IconButton>
                                                <Tooltip title={_t('Hide job from results')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleHide(e, job.id)}
                                                        sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                                                    >
                                                        <BlockIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>
                                            {job.company?.title || 'Unknown Company'}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                <LocationOnIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                <Typography variant="caption" color="text.secondary">
                                                    {getLocation(job)}
                                                </Typography>
                                            </Box>
                                            {salary && (
                                                <>
                                                    <Typography variant="caption" color="text.disabled">·</Typography>
                                                    <Typography variant="caption" fontWeight={600} color="success.dark">
                                                        {salary}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {job.jobtypes?.slice(0, 2).map((type) => (
                                                    <Chip
                                                        key={type.id}
                                                        label={Utils.normalizeText(type.title)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.72rem', height: '24px' }}
                                                    />
                                                ))}
                                                {job.schedules?.slice(0, 1).map((schedule) => (
                                                    <Chip
                                                        key={schedule.id}
                                                        label={Utils.normalizeText(schedule.title)}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{ fontSize: '0.72rem', height: '24px' }}
                                                    />
                                                ))}
                                            </Box>
                                            <Typography variant="caption" color="text.disabled">
                                                {Utils.timeAgo(job.updated_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {/* Load More Button */}
            {hasNextPage && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        onClick={() => fetchNextPage()}
                        disabled={isFetching}
                        fullWidth
                    >
                        {isFetching ? <CircularProgress size={20} /> : 'Load more'}
                    </Button>
                </Box>
            )}

            {/* Infinite scroll trigger */}
            <Box ref={observerTarget} sx={{ height: 20, mt: 2 }} />

            {isFetching && hasNextPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    );
}

export default HomeJobsSidebar;
